"use client";

import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFromIndexedDB, getFromLocalStorage } from "@/utils/storage";
import { hydrateAuth } from "@/redux/slices/authSlice";
import {
  syncCartFromLocal,
  fetchCartAction,
  syncCartOnLoginAction,
} from "@/redux/slices/cartSlice";
import { syncWishlist } from "@/redux/slices/wishlistSlice";
import { syncOrders } from "@/redux/slices/orderSlice";
import { findAllActiveDiscount } from "@/modules/discount/discount.service";
import { AppDispatch, RootState } from "@/redux/store";
import { detectLocationAction } from "@/redux/slices/locationSlice";
import { isInternationalUserSync } from "@/utils/location";
import {
  getCartItemEffectivePricing,
  getProductUnitPrice,
} from "@/utils/priceUtils";

export default function HydrationWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [isHydrated, setIsHydrated] = useState(false);
  const { isCustomerAuthenticated, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const hydrate = async () => {
      // 0. Detect user location and fetch active discounts
      dispatch(detectLocationAction());
      dispatch(findAllActiveDiscount());
      if (typeof window === "undefined") return;

      // 1. Hydrate Auth State from localStorage
      const role = getFromLocalStorage("nakman_role") as
        | "user"
        | "customer"
        | null;
      const token = getFromLocalStorage("nakman_token");
      const userDataStr = getFromLocalStorage("nakman_user_data");

      let userFromLocal = null;
      if (userDataStr) {
        try {
          userFromLocal = JSON.parse(userDataStr);
        } catch (e) {
          console.error("Failed to parse user data from localStorage", e);
        }
      }

      const isFirstLoginLocal = getFromLocalStorage("IsFirstLogin");
      const isFirstOrder =
        isFirstLoginLocal === null
          ? true
          : typeof isFirstLoginLocal === "string"
          ? isFirstLoginLocal.trim().toLowerCase() === "true"
          : Boolean(isFirstLoginLocal);

      dispatch(
        hydrateAuth({
          isUserAuthenticated: role === "user",
          isCustomerAuthenticated: role === "customer",
          role,
          token,
          user: userFromLocal
            ? {
                ...userFromLocal,
                isFirstOrder: isFirstOrder,
              }
            : null,
        }),
      );

      // 2. Hydrate cart from localStorage (Guest & Customer initial load)
      const isIntl = isInternationalUserSync();
      const cartDataStr = getFromLocalStorage("nakman_cart");
      let initialLocalCart: any[] = [];
      if (cartDataStr) {
        try {
          const cartData = JSON.parse(cartDataStr);
          if (Array.isArray(cartData)) {
            // Normalize legacy items and heal double-hiked prices
            initialLocalCart = cartData.map((item: any) => {
              const qty = Number(item.Qty || item.qty || 1);
              const { basePrice, productPrices } = getCartItemEffectivePricing(
                item,
                isIntl,
              );
              const unitPrice = getProductUnitPrice(
                basePrice,
                productPrices,
                qty,
              );
              return {
                ProductID: Number(item.ProductID || item.id || item.productID),
                ProductName:
                  item.ProductName ||
                  item.name ||
                  item.productName ||
                  "Unknown Product",
                BasePrice: basePrice,
                Price: unitPrice,
                Qty: isNaN(qty) ? 1 : qty,
                TotalPrice: isNaN(qty * unitPrice) ? 0 : qty * unitPrice,
                URL: item.URL || item.url,
                CartID: item.CartID || item.cartID,
                GSTPer: item.GSTPer || item.gstPer,
                MainImage: item.MainImage || item.mainImage,
                AvailableQty: item.AvailableQty,
                ProductPrices: productPrices,
                MinimumQty: item.MinimumQty,
                IsCostToCost: item.IsCostToCost,
                RawBasePrice: item.RawBasePrice,
                RawProductPrices: item.RawProductPrices,
              };
            });
            localStorage.setItem("nakman_cart", JSON.stringify(initialLocalCart));
            dispatch(syncCartFromLocal(initialLocalCart));
          }
        } catch (e) {
          console.error("Failed to parse cart data", e);
        }
      }

      // 3. Trigger Hybrid Sync if customer is authenticated
      if (role === "customer" && userFromLocal?.id) {
        // Only merge items that were added as a guest (no CartID)
        const guestItems = initialLocalCart.filter(item => !item.CartID);
        if (guestItems.length > 0) {
          dispatch(syncCartOnLoginAction({ 
            customerID: Number(userFromLocal.id), 
            localItems: guestItems 
          }));
        } else {
          // Otherwise just fetch latest from backend
          dispatch(fetchCartAction(Number(userFromLocal.id)));
        }
      }

      // 4. Hydrate other states from IndexedDB
      const email = userFromLocal?.email;
      if (email) {
        const wishlistData = await getFromIndexedDB<any[]>(
          `nakman_wishlist_${email}`,
        );
        if (wishlistData) dispatch(syncWishlist(wishlistData));

        const orderData = await getFromIndexedDB<any[]>(
          `nakman_orders_${email}`,
        );
        if (orderData) dispatch(syncOrders(orderData));
      }

      setIsHydrated(true);
    };

    hydrate();
  }, [dispatch]);

  // Session-level login sync
  const lastAuthRef = useRef(isCustomerAuthenticated);
  useEffect(() => {
    if (!lastAuthRef.current && isCustomerAuthenticated && user?.id) {
       // Just signed in during this session
       const cartDataStr = getFromLocalStorage("nakman_cart");
       let localItems = [];
       if (cartDataStr) {
         try {
           const parsed = JSON.parse(cartDataStr);
           if (Array.isArray(parsed)) localItems = parsed;
         } catch (e) {}
       }
       dispatch(syncCartOnLoginAction({ 
         customerID: Number(user.id), 
         localItems: localItems 
       }));
    }
    lastAuthRef.current = isCustomerAuthenticated;
  }, [isCustomerAuthenticated, user?.id, dispatch]);

  if (!isHydrated) {
    return null; 
  }

  return <>{children}</>;
}
